"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Head from "next/head";
import Image from "next/image";
import { debounce } from "lodash";
import styles from "./PDFViewer.module.css";

import type { PdfDocumentData } from "./../../../interfaces";
import { HandleScroll, fetchPdfInfo } from "./../../../functions";
import type { FormatType } from "./../../../interfaces";
import { get_pdf_urls, get_pdf_baseId_from_url } from "./../../../functions";
const pageTxt = (id: string, n: number) =>
  `/pdfs/${id}/pages/page_${n}/${id}_page_${n}.txt`;

const pagePng = (id: string, n: number) =>
  `/pdfs/${id}/images/page_${n}/${id}_page_${n}.png`;
type FitMode = "page-fit" | "page-width" | "custom";

interface PdfViewerProps {
  baseId?: string;
  rootPath?: string;
  rootString?: string | null;
  searchQuery?: string;
}
// Put these near the top of the file (below imports)
const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[m] as string));

const highlightHTML = (text: string, query: string) => {
  if (!query) return `<pre>${escapeHtml(text)}</pre>`;
  const q = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // escape regex
  const rx = new RegExp(`(${q})`, "gi");
  return `<pre>${escapeHtml(text).replace(rx, "<mark>$1</mark>")}</pre>`;
};
const PDFViewer: React.FC<PdfViewerProps> = ({
  baseId,
  rootPath = "pdfs",
  rootString = null,
  searchQuery = "",
}) => {
  // ---------- State ----------
  const [data, setData] = useState<PdfDocumentData | null>(null);
  const [directories, setDirectories] = useState<string[]>([]);
  const [selectedDirIndex, setSelectedDirIndex] = useState<number>(-1);
  const [format, setFormat] = useState<FormatType>("pdf");
  const [availableFormats] = useState<FormatType[]>(["pdf", "images", "text"]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showThumbnails, setShowThumbnails] = useState<boolean>(true);

  // viewer controls
  const [fitMode, setFitMode] = useState<FitMode>("page-fit");
  const [zoom, setZoom] = useState<number>(100); // percent when fitMode === 'custom'
  const [rotation, setRotation] = useState<number>(0); // 0, 90, 180, 270

  // search
  const [localQuery, setLocalQuery] = useState<string>(searchQuery || "");
  const [searching, setSearching] = useState<boolean>(false);
  const [searchHits, setSearchHits] = useState<Array<{ page: number; snippet: string }>>([]);
  const pageTextCache = useRef<Map<number, string>>(new Map());

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const userScrolling = useRef(false);

  // ---------- Helpers ----------
  const clampPage = useCallback(
    (p: number) => Math.max(1, Math.min(totalPages || 1, p)),
    [totalPages]
  );

  const rotateNext = () => setRotation((r) => (r + 90) % 360);
  const rotatePrev = () => setRotation((r) => (r + 270) % 360);

  const { pdfInfoUrl, pdfUrl, txtUrl, imgUrl, pdfSiteUrl } = useMemo(
    () => (baseId ? get_pdf_urls(baseId) : {
      pdfUrl: "", txtUrl: "", imgUrl: "", pdfSiteUrl: "", pdfInfoUrl: ""
    }),
    [baseId]
  );

  const buildPdfViewerSrc = useCallback(
    (page: number) => {
      const zoomFragment =
        fitMode === "page-fit" || fitMode === "page-width"
          ? fitMode
          : String(Math.max(25, Math.min(400, zoom)));
      const rotFragment = rotation ? `&rotation=${rotation}` : "";
      const searchFragment = localQuery ? `&search=${encodeURIComponent(localQuery)}&highlight=all` : "";
      // NOTE: PDF.js honors search; Chrome native viewer may ignore it.
      return `${pdfUrl}#toolbar=1&page=${clampPage(page)}&zoom=${zoomFragment}${rotFragment}${searchFragment}`;
    },
    [pdfUrl, fitMode, zoom, rotation, clampPage, localQuery]
  );

  // ---------- Fetch metadata for single doc ----------
  // The metadata is the STATIC, same-origin /pdfs/<baseId>/info.json file
  // ({combined_keywords, document_summary, …}). It may be absent for some
  // documents — degrade to minimal metadata derived from the baseId and keep
  // the viewer working instead of erroring out.
  useEffect(() => {
    if (!baseId) return;

    async function fetchMetadata() {
      try {
        setIsLoading(true);
        setError(null);
        const d: any = (await fetchPdfInfo(pdfInfoUrl)) || {};

        const pdfKeywords =
          d.combined_keywords || d.keywords_keybert?.map((x: any) => x[0]) || [];
        const keywordsArr = Array.isArray(pdfKeywords) ? pdfKeywords : [];

        const parsed: PdfDocumentData = {
          pdfTitle: d.document_title || d.filename || (baseId || "").split("/").pop() || baseId,
          pdfSummary: d.document_summary || d.summary || "No summary available.",
          pdfKeywords: keywordsArr,
          pdfKeywordString: keywordsArr.join(", "),
          pdfUrl,
          pdfInfoUrl,
          pdfAuthor: d.author || "The Daily Dialectics",
          pdfDescription: d.document_summary || d.summary,
          // Real date or nothing — never fabricate "now" into the schema.
          pdfDatePublished: d.date || d.datePublished || "",
          pdfSiteUrl,
          pdfThumbnailUrl: d.thumbnailUrl || undefined,
          baseId,
        };

        setData(parsed);
        if (typeof d.num_pages === "number" && d.num_pages > 0) {
          setTotalPages(d.num_pages);
          setCurrentPage(1);
        } else {
          setTotalPages(d.num_pages || 1);
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchMetadata();
  }, [baseId, pdfInfoUrl, pdfSiteUrl]);

  // NOTE: the old "multi-document" mode (no baseId) browsed a directory listing
  // served by the retired api subdomain — that API is dead (2026-07-07), so
  // the mode was removed. Without a baseId this component renders nothing.

  // ---------- Scroll handling (images/text modes) ----------
  const handleScrollEvent = useCallback(
    debounce(() => {
      HandleScroll(
        format,
        selectedDirIndex,
        totalPages,
        userScrolling,
        iframeRef,
        currentPage,
        setCurrentPage,
        imageContainerRef
      );
    }, 100),
    [format, selectedDirIndex, totalPages, currentPage]
  );

  // ---------- Keyboard shortcuts ----------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target && (e.target as HTMLElement).tagName === "INPUT") return;
      if (e.key === "ArrowLeft") setCurrentPage((p) => clampPage(p - 1));
      if (e.key === "ArrowRight") setCurrentPage((p) => clampPage(p + 1));
      if (e.key === "t") setShowThumbnails((v) => !v);
      if (e.key === "w") setFitMode("page-width");
      if (e.key === "f") setFitMode("page-fit");
      if (e.key === "+") setZoom((z) => Math.min(400, z + 10));
      if (e.key === "-") setZoom((z) => Math.max(25, z - 10));
      if (e.key === "r") rotateNext();
      if (e.key === "R") rotatePrev();
      if (e.key === "/") {
        e.preventDefault();
        const el = document.getElementById("pdf-search-input") as HTMLInputElement | null;
        el?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clampPage]);
  const TextPage: React.FC<{ id: string; page: number; query: string }> = ({ id, page, query }) => {
    const [html, setHtml] = useState<string>("");
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      let abort = false;
      (async () => {
        try {
          const res = await fetch(`/pdfs/${id}/pages/page_${page}/${id}_page_${page}.txt`, { cache: "force-cache" });
          const txt = res.ok ? await res.text() : "";
          if (!abort) setHtml(highlightHTML(txt, query));
        } catch {
          if (!abort) setHtml("<pre>(failed to load text)</pre>");
        }
      })();
      return () => { abort = true; };
    }, [id, page, query]);

    // after render, scroll to first <mark>
    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const first = el.querySelector("mark") as HTMLElement | null;
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [html]);

    return (
      <div
        ref={containerRef}
        className={`${styles.textFrame} w-full h-full overflow-auto p-3 font-mono text-sm whitespace-pre-wrap`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  // ---------- Search (lazy fetch of text pages) ----------
  const fetchPageText = useCallback(async (page: number) => {
    const resolvedBaseId = get_pdf_baseId_from_url(pdfSiteUrl) || baseId;
    if (!resolvedBaseId) return "";
    baseId = get_pdf_baseId_from_url(pdfSiteUrl) || baseId
    if (pageTextCache.current.has(page)) return pageTextCache.current.get(page)!;
    try {

      const res = await fetch(`/pdfs/${resolvedBaseId}/pages/page_${page}/${resolvedBaseId}_page_${page}.txt`, { cache: "force-cache" });
      if (!res.ok) return "";
      const txt = await res.text();
      pageTextCache.current.set(page, txt || "");
      return txt || "";
    } catch {
      return "";
    }
  }, [pdfSiteUrl, baseId]);

  const runSearch = useCallback(
    debounce(async (q: string) => {
      if (!q || !q.trim()) {
        setSearchHits([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      const lc = q.trim().toLowerCase();
      const hits: Array<{ page: number; snippet: string }> = [];
      const maxPages = Math.max(1, totalPages);
      // breadth-first: sample currentPage window first for UX
      const order: number[] = [];
      for (let r = 0; r < maxPages; r++) {
        const a = currentPage - r;
        const b = currentPage + r;
        if (a >= 1) order.push(a);
        if (b <= maxPages && b !== a) order.push(b);
      }
      for (const p of order) {
        const txt = await fetchPageText(p);
        if (!txt) continue;
        const idx = txt.toLowerCase().indexOf(lc);
        if (idx !== -1) {
          const start = Math.max(0, idx - 60);
          const end = Math.min(txt.length, idx + lc.length + 60);
          const snippet = txt.slice(start, end).replace(/\s+/g, " ");
          hits.push({ page: p, snippet });
          if (hits.length >= 50) break; // cap results
        }
      }
      setSearchHits(hits);
      setSearching(false);
    }, 250),
    [currentPage, totalPages, fetchPageText]
  );

  useEffect(() => {
    setLocalQuery(searchQuery || "");
  }, [searchQuery]);

  useEffect(() => {
    runSearch(localQuery);
  }, [localQuery, runSearch]);

  // ---------- SEO Schema ----------
  const seoSchema =
    data && {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: data.pdfTitle,
      author: data.pdfAuthor,
      // Only a real date from info.json — omitted entirely when unknown.
      ...(data.pdfDatePublished ? { datePublished: data.pdfDatePublished } : {}),
      description: data.pdfDescription || data.pdfSummary,
      keywords: data.pdfKeywordString,
      url: data.pdfSiteUrl || data.pdfUrl,
      thumbnailUrl: data.pdfThumbnailUrl,
      fileFormat: "application/pdf",
      encodingFormat: "application/pdf",
    };

  // ---------- Render ----------
  // No baseId → nothing to show (the API-backed directory browser is retired).
  if (!baseId) return null;
  if (error) return <p className="text-red-600">⚠️ {error}</p>;
  if (!data && !directories.length) return <p>Loading...</p>;

  const iframeSrc = buildPdfViewerSrc(currentPage);

  return (
    <>
      {data && (
        <Head>
          <title>{data.pdfTitle}</title>
          <meta name="description" content={(data.pdfDescription || "").slice(0, 160)} />
          <meta name="keywords" content={data.pdfKeywordString || ""} />
          {/* OpenGraph / Twitter */}
          <meta property="og:type" content="article" />
          <meta property="og:title" content={data.pdfTitle} />
          <meta property="og:description" content={(data.pdfDescription || "").slice(0, 200)} />
          {data.pdfThumbnailUrl && <meta property="og:image" content={data.pdfThumbnailUrl} />}
          <meta name="twitter:card" content="summary_large_image" />
          <link rel="canonical" href={data.pdfSiteUrl || data.pdfUrl} />
          {seoSchema && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(seoSchema) }}
            />
          )}
        </Head>
      )}

      <div className={`${styles.viewer} min-h-screen bg-gray-50 text-gray-900 py-6 px-4 md:px-10`}>
        <div className="max-w-7xl mx-auto grid grid-cols-12 gap-4">
          {/* ---------- Sidebar (metadata + search) ---------- */}
          <aside className="col-span-12 lg:col-span-3 bg-white rounded-xl shadow-md p-4 h-max">
            {data && (
              <>
                <h1 className="text-xl font-semibold mb-2">{data.pdfTitle}</h1>
                {data.pdfAuthor && (
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Author:</strong> {data.pdfAuthor}
                  </p>
                )}
                {data.pdfDatePublished && (
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Published:</strong>{" "}
                    {new Date(data.pdfDatePublished).toLocaleDateString()}
                  </p>
                )}

                {data.pdfSummary && (
                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">{data.pdfSummary}</p>
                )}

                {!!(data.pdfKeywords || []).length && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(data.pdfKeywords || []).map((kw) => (
                      <span
                        key={kw}
                        className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs"
                      >
                        {kw}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Search */}
            <div className="mt-2">
              <label htmlFor="pdf-search-input" className="text-sm font-semibold">
                Search document
              </label>
              <input
                id="pdf-search-input"
                type="text"
                value={localQuery}
                onChange={(e) => setLocalQuery(e.target.value)}
                placeholder="Find text… ( / to focus )"
                className={`${styles.searchInput} w-full mt-1 p-2 text-sm`}
              />
              <div className="mt-2 max-h-64 overflow-auto border rounded">
                {searching ? (
                  <p className="p-2 text-sm text-gray-500">Searching…</p>
                ) : !localQuery ? (
                  <p className="p-2 text-sm text-gray-400">Type to search</p>
                ) : searchHits.length === 0 ? (
                  <p className="p-2 text-sm text-gray-500">No matches</p>
                ) : (
                  <ul className="divide-y">
                    {searchHits.map((h, i) => (
                      <li
                        key={`${h.page}-${i}`}
                        className="p-2 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setCurrentPage(h.page)}
                        title={`Go to page ${h.page}`}
                      >
                        <div className="text-xs text-gray-500 mb-1">Page {h.page}</div>
                        <div className="text-sm">…{h.snippet.replace(/\s+/g, " ")}…</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </aside>

          {/* ---------- Main viewer ---------- */}
          <section className="col-span-12 lg:col-span-9">
            <div className={`${styles.surface} bg-white rounded-xl shadow-md p-4`}>
              {/* Controls */}
              <div className={`${styles.controls} flex flex-wrap items-center gap-3 mb-3`}>
                <div className="flex items-center gap-2">
                  <label className="text-sm">Format</label>
                  <select
                    value={format}
                    onChange={(e) => setFormat(e.target.value as FormatType)}
                    className="border rounded p-1 text-sm"
                  >
                    {availableFormats.map((fmt) => (
                      <option key={fmt} value={fmt}>
                        {fmt}
                      </option>
                    ))}
                  </select>
                </div>

                {format === "pdf" && (
                  <>
                    <div className="flex items-center gap-2">
                      <label className="text-sm">Fit</label>
                      <select
                        value={fitMode}
                        onChange={(e) => setFitMode(e.target.value as FitMode)}
                        className="border rounded p-1 text-sm"
                      >
                        <option value="page-fit">page-fit</option>
                        <option value="page-width">page-width</option>
                        <option value="custom">custom</option>
                      </select>
                    </div>
                    {fitMode === "custom" && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm">Zoom</label>
                        <input
                          type="number"
                          min={25}
                          max={400}
                          value={zoom}
                          onChange={(e) => setZoom(Number(e.target.value || 100))}
                          className="w-20 border rounded p-1 text-sm"
                        />
                        <span className="text-sm">%</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <button
                        className="px-2 py-1 border rounded text-sm"
                        onClick={rotatePrev}
                        title="Rotate 270°"
                      >
                        ↶
                      </button>
                      <button
                        className="px-2 py-1 border rounded text-sm"
                        onClick={rotateNext}
                        title="Rotate 90°"
                      >
                        ↷
                      </button>
                    </div>
                  </>
                )}

                {totalPages > 1 && (
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => clampPage(p - 1))}
                      disabled={currentPage === 1}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      Prev
                    </button>
                    <div className="text-sm">
                      Page{" "}
                      <input
                        type="number"
                        min={1}
                        max={totalPages}
                        value={currentPage}
                        onChange={(e) => setCurrentPage(clampPage(Number(e.target.value || 1)))}
                        className="w-16 text-center border rounded p-0.5"
                      />{" "}
                      / {totalPages}
                    </div>
                    <button
                      onClick={() => setCurrentPage((p) => clampPage(p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
              {/* Viewer */}
              <div className={`${styles.viewport} aspect-[3/4] w-full border rounded-lg overflow-hidden mb-3`}>
                {format === "pdf" ? (
                  // ---- PDF (iframe) ----
                  <iframe
                    key={`${currentPage}-${fitMode}-${zoom}-${rotation}-${localQuery}`} // reloads when search changes
                    ref={iframeRef}
                    src={iframeSrc}  // buildPdfViewerSrc already includes #page & #search fragments
                    title={data?.pdfTitle}
                    className={`${styles.iframe} w-full h-full border-none`}
                    loading="lazy"
                  />
                ) : format === "images" ? (
                  // ---- IMAGES (PNG pages) ----
                  <div
                    ref={imageContainerRef}
                    className={`${styles.imageScroller} overflow-y-auto h-full p-2 flex flex-col items-center`}
                    onScroll={handleScrollEvent}
                  >
                    {Array.from({ length: totalPages }, (_, i) => (
                      <div key={i} className="mb-2" style={{ transform: `rotate(${rotation}deg)` }}>
                        <Image
                          src={`/pdfs/${baseId}/images/page_${i + 1}/${baseId}_page_${i + 1}.png`}
                          alt={`Page ${i + 1}`}
                          width={900}
                          height={1200}
                          className={styles.pageImage}
                          unoptimized
                          onError={() => console.warn(`Image page ${i + 1} failed`)}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  // ---- TEXT (rendered + highlighted) ----
                  baseId ? (
                    <TextPage id={baseId} page={currentPage} query={localQuery} />
                  ) : null
                )}
              </div>


              {/* Thumbnails */}
              {showThumbnails && totalPages > 1 && (
                <div className={`${styles.thumbs} w-full overflow-x-auto py-2 border-t`}>
                  <div className={`${styles.thumbRow} flex gap-2`}>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`relative flex-shrink-0 border rounded overflow-hidden ${styles.thumbBtn} ${currentPage === i + 1 ? styles.thumbActive : ""
                          }`}
                        title={`Page ${i + 1}`}
                        style={{ width: 96, height: 128 }}
                      >
                        <Image
                          src={`/pdfs/${baseId}/images/page_${i + 1}/${baseId}_page_${i + 1}.png`}
                          alt={`Page ${i + 1}`}
                          fill
                          sizes="96px"
                          unoptimized
                          onError={(e) => {
                            (e.currentTarget as any).style.display = "none";
                          }}
                        />
                        <span className="absolute bottom-0 right-0 bg-black/60 text-white text-xs px-1 rounded-tl">
                          {i + 1}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer toggles */}
              <div className="flex items-center justify-between mt-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showThumbnails}
                    onChange={() => setShowThumbnails((v) => !v)}
                  />
                  Thumbnails
                </label>
                <div className="text-xs text-gray-500">
                  Shortcuts: ←/→ page • f fit-page • w fit-width • +/− zoom • r/R rotate • / search
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default PDFViewer;
